/**
 * LTL "ship with" — the client side of a linked group of outbound loads that
 * ride on one truck (docs/plans/2026-09-21-ltl-ship-with.md §6.1).
 *
 * Group identity lives on the shipment: `shipWithGroupId` plus a denormalized
 * `shipWithIds` listing every member id, self included, sorted (AD-1). These
 * helpers are the only place the surfaces read that list from, so the rule
 * "linked means more than one id" is written once.
 *
 * Link and unlink go through the processOutbound lambda (`/outboundShipWith`,
 * AD-7); the group fields are lambda-owned and never written from a form.
 */

import { API, graphqlOperation } from "aws-amplify";

import { getOutboundShipmentShipWith } from "../graphql/outbound/custom-queries";
import { logError } from "./appUtils";

const SHIP_WITH_PATH = "/outboundShipWith";

/** Fetched sibling projections stay fresh this long — long enough to cover a
 * hover, short enough that a link made on another tab shows within a minute. */
const MEMBER_CACHE_TTL_MS = 60 * 1000;

/** Drop blanks and repeats but keep the caller's order. */
const uniqueIds = (ids) => {
  const seen = new Set();
  const out = [];
  for (const id of Array.isArray(ids) ? ids : []) {
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};

/** True when the shipment belongs to a group of two or more loads. Every new
 * code path gates on this (AD-11), so a singleton behaves exactly as today. */
export const isLinked = (shipment) =>
  Array.isArray(shipment?.shipWithIds) && shipment.shipWithIds.length > 1;

/** The other members of the shipment's group — its own id removed. */
export const shipWithSiblingIds = (shipment) =>
  uniqueIds(shipment?.shipWithIds).filter((id) => id !== shipment?.id);

/**
 * "3 loads · PO 123, 456, 789" from a list of member projections. Members
 * that did not load (no poNumber) still count as loads but are left off the PO
 * list rather than printed as a blank.
 */
export const shipWithLabel = (members) => {
  const list = Array.isArray(members) ? members : [];
  const count = list.length;
  const poNumbers = list.map((m) => m?.poNumber).filter(Boolean);
  const head = `${count} ${count === 1 ? "load" : "loads"}`;
  return poNumbers.length ? `${head} · PO ${poNumbers.join(", ")}` : head;
};

/**
 * Every outbound a check-in controls. Linked rows carry `outboundIds`; rows
 * written before the feature, and single-load rows, carry only `outboundId`.
 */
export const groupCheckInShipmentIds = (checkIn) => {
  if (checkIn?.outboundIds?.length) return uniqueIds(checkIn.outboundIds);
  return checkIn?.outboundId ? [checkIn.outboundId] : [];
};

/**
 * The lambda answers a rejected link with `{ error: { message, userFacing } }`
 * on a 200, the same envelope `/outboundUpdate` uses. Throwing an Error that
 * keeps both fields lets a save button show a userFacing message verbatim
 * (`notify(err.message)`) and route anything else through logError.
 */
const toLambdaError = (envelope) => {
  const message =
    (typeof envelope === "string" ? envelope : envelope?.message) ||
    "The request could not be completed.";
  const error = new Error(message);
  error.userFacing = envelope?.userFacing === true;
  return error;
};

const postShipWith = async (action, outboundIds, { reloadYardId } = {}) => {
  const response = await API.post("transREST", SHIP_WITH_PATH, {
    body: {
      action,
      outboundIds,
      // Same identity the schedule save sends: the lambda stamps `by` on the
      // history note from it.
      user: window.appUser?.email,
      reloadYardId: reloadYardId ?? window.appUser?.reloadYardId,
    },
  });
  if (response?.error) throw toLambdaError(response.error);
  return response;
};

/**
 * Link the given loads into one group (merging into at most one existing
 * group — the lambda enforces the rest of AD-7). Resolves to
 * `{ ok: true, groupId, shipWithIds }`.
 */
export const linkOutbounds = (ids, options) =>
  postShipWith("link", uniqueIds(ids), options);

/** Remove one load from its group. Its appointment is cleared server-side —
 * it needs its own truck now. Resolves to `{ ok: true, groupId, shipWithIds }`. */
export const unlinkOutbound = (id, options) =>
  postShipWith("unlink", uniqueIds([id]), options);

/** One narrow read; a member that cannot be read is reported, not thrown. */
const fetchMember = async (id) => {
  try {
    const response = await API.graphql(
      graphqlOperation(getOutboundShipmentShipWith, { id }),
    );
    return response?.data?.getOutboundShipment || { id, unavailable: true };
  } catch (error) {
    logError("utils:shipWith:fetchShipWithMembers", error, false);
    return { id, unavailable: true };
  }
};

/**
 * Read the projection of every member, in the order given (N ≤ 10, so one
 * call each, in parallel). Never rejects: a member that fails to load comes
 * back as `{ id, unavailable: true }` so the caller can still count it.
 */
export const fetchShipWithMembers = (ids) =>
  Promise.all(uniqueIds(ids).map(fetchMember));

/** The cache key — the sorted id string, so order of the input never matters. */
export const shipWithCacheKey = (ids) => uniqueIds(ids).sort().join(",");

const memberCache = new Map();

/**
 * Same as fetchShipWithMembers, shared across the page: keyed by the sorted
 * id string with a 60 s TTL, and an in-flight promise is handed to every
 * caller that arrives before it settles. A list of thirty linked rows costs
 * nothing until a badge is hovered, and a hovered group is fetched once.
 * A result with an unavailable member is not kept, so the next hover retries.
 */
export const fetchShipWithMembersCached = (ids) => {
  const key = shipWithCacheKey(ids);
  if (!key) return Promise.resolve([]);

  const now = Date.now();
  const hit = memberCache.get(key);
  if (hit && hit.expiresAt > now) return hit.promise;

  const promise = fetchShipWithMembers(key.split(","));
  const entry = { promise, expiresAt: now + MEMBER_CACHE_TTL_MS };
  memberCache.set(key, entry);
  promise.then((members) => {
    if (
      members.some((m) => m?.unavailable) &&
      memberCache.get(key) === entry
    ) {
      memberCache.delete(key);
    }
  });
  return promise;
};

/** Forget cached members — one group when ids are given, everything otherwise.
 * The aside's refresh calls this so the badge tooltip stops showing the
 * pre-unlink list. */
export const clearShipWithMemberCache = (ids) => {
  if (ids === undefined) {
    memberCache.clear();
    return;
  }
  const key = shipWithCacheKey(ids);
  if (key) memberCache.delete(key);
};
